import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Grid, Card, CardContent, Typography, Button, Chip, Alert, CircularProgress, Stack,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { apiService } from '../services/api';

interface Plan {
  id: number;
  code: string;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number | null;
  currency: string;
  trialDays: number;
  isPopular: boolean;
  features?: string[];
}

interface StatusDto {
  status: string;
  subscriptionActive: boolean;
  planCode?: string;
  planName?: string;
  daysRemaining: number;
  isTrialPeriod: boolean;
  invoicingTier?: string;
  invoicingQuota?: number;
  pendingCoupon?: { code: string; discountPercent: number; durationMonths: number } | null;
}

interface InvoicingTier {
  tier: string;
  quota: number;
  monthlyPrice: number;
  label: string;
  annualEstimate: number;
}

export default function SubscriptionUpgrade() {
  const navigate = useNavigate();
  // ?from=<módulo>: llegó por el candado de un módulo pago estando en el plan Gratis.
  const [searchParams] = useSearchParams();
  const lockedFrom = searchParams.get('from');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [status, setStatus] = useState<StatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [invoicingTiers, setInvoicingTiers] = useState<InvoicingTier[]>([]);
  const [selectedTier, setSelectedTier] = useState<string>('none');
  const [savingTier, setSavingTier] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const api = (apiService as any).api;
        const [plansRes, statusRes, tiersRes] = await Promise.all([
          api.get('/subscription-plans'),
          api.get('/checkout/status'),
          api.get('/checkout/invoicing-tiers'),
        ]);
        // El Gratis no se contrata: es el plan de toda cuenta nueva.
        // Destacado: siempre el primer plan pago, no el isPopular de la base.
        setPlans((plansRes.data || []).filter((p: Plan) => p.code !== 'free').map((p: Plan, i: number) => ({ ...p, isPopular: i === 0 })));
        setStatus(statusRes.data);
        setInvoicingTiers(tiersRes.data || []);
        setSelectedTier(statusRes.data?.invoicingTier || 'none');
      } catch (e: any) {
        setError(e?.response?.data?.error || 'No pudimos cargar los planes');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSelectTier = async (tier: string) => {
    setSavingTier(true);
    setError('');
    try {
      const api = (apiService as any).api;
      await api.post('/checkout/invoicing-tier', { tier });
      setSelectedTier(tier);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No pudimos actualizar la facturación');
    } finally {
      setSavingTier(false);
    }
  };

  const handleSubscribe = async (planCode: string) => {
    setError('');
    setSubscribing(planCode);
    try {
      const api = (apiService as any).api;
      const res = await api.post('/checkout/create-subscription', { planCode });
      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        setError('El provider no devolvió una URL de checkout');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error creando la suscripción');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, bgcolor: '#f5f7fb', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Elegí tu plan
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {status?.status === 'Free'
            ? 'Estás en el plan Gratis: ventas y stock con hasta 100 productos y 1 usuario. Pasate a un plan pago para sumar productos, usuarios, compras, producción, canales y más.'
            : status?.isTrialPeriod && status.daysRemaining > 0
            ? `Te quedan ${status.daysRemaining} día${status.daysRemaining === 1 ? '' : 's'} de prueba gratis. Suscribite ahora para no perder acceso.`
            : status?.status === 'Demo' || status?.status === 'Expired'
            ? 'Tu prueba terminó. Elegí un plan para reactivar la plataforma.'
            : 'Cambiá tu plan cuando quieras.'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {lockedFrom && status?.status === 'Free' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Esa sección no está incluida en el plan Gratis. Elegí un plan para desbloquearla.
          </Alert>
        )}

        {status?.pendingCoupon && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Tenés el código {status.pendingCoupon.code}: {status.pendingCoupon.discountPercent}% OFF{' '}
            {status.pendingCoupon.durationMonths === 1 ? 'en tu primer pago mensual' : `en tus primeros ${status.pendingCoupon.durationMonths} pagos mensuales`}.
            Se aplica solo al suscribirte con Mercado Pago.
          </Alert>
        )}

        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.code}>
              <Card sx={{ height: '100%', position: 'relative', border: plan.isPopular ? '2px solid' : '1px solid', borderColor: plan.isPopular ? 'primary.main' : 'divider' }}>
                {plan.isPopular && (
                  <Chip label="Más elegido" color="primary" size="small" sx={{ position: 'absolute', top: 12, right: 12 }} />
                )}
                {plan.code === 'ia' && !plan.isPopular && (
                  <Chip label="Nuevo" color="secondary" size="small" sx={{ position: 'absolute', top: 12, right: 12 }} />
                )}
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" fontWeight={700}>{plan.name}</Typography>
                  {plan.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {plan.description}
                    </Typography>
                  )}
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
                    {plan.originalPrice && plan.originalPrice > plan.price && (
                      <Typography variant="body1" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                        ${plan.originalPrice.toLocaleString('es-AR')}
                      </Typography>
                    )}
                    <Typography variant="h4" color="primary.main">
                      ${plan.price.toLocaleString('es-AR')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {plan.currency} /mes
                    </Typography>
                  </Stack>
                  {plan.originalPrice && plan.originalPrice > plan.price && (
                    <Chip size="small" color="error" label={`-${Math.round((1 - plan.price / plan.originalPrice) * 100)}%`} sx={{ mb: 1.5 }} />
                  )}
                  {plan.features && plan.features.length > 0 && (
                    <Stack spacing={0.75} sx={{ mb: 2 }}>
                      {plan.features.map((f, idx) => (
                        <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                          <CheckCircle fontSize="small" color="success" sx={{ mt: 0.2 }} />
                          <Typography variant="body2">{f}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  <Button
                    fullWidth
                    variant={plan.isPopular ? 'contained' : 'outlined'}
                    disabled={subscribing === plan.code}
                    onClick={() => handleSubscribe(plan.code)}
                    sx={{ mt: 1 }}
                  >
                    {subscribing === plan.code ? <CircularProgress size={22} /> : status?.planCode === plan.code && status.subscriptionActive ? 'Plan actual' : 'Suscribirme'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {invoicingTiers.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>Facturación electrónica (ARCA)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              La facturación es un costo adicional al plan. Elegí cuántas facturas necesitás por mes.
            </Typography>
            <Grid container spacing={3}>
              {invoicingTiers.map((t) => {
                const selected = selectedTier === t.tier;
                return (
                  <Grid item xs={12} sm={6} md={3} key={t.tier}>
                    <Card sx={{ height: '100%', border: selected ? '2px solid' : '1px solid', borderColor: selected ? 'primary.main' : 'divider' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ minHeight: 48 }}>{t.label}</Typography>
                        <Typography variant="h5" color="primary.main" sx={{ my: 1 }}>
                          {t.monthlyPrice > 0 ? `$ ${t.monthlyPrice.toLocaleString('es-AR')}` : '$ 0'}
                          <Typography component="span" variant="body2" color="text.secondary"> /mes</Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t.quota > 0 ? `Hasta ${t.quota.toLocaleString('es-AR')} facturas por mes` : 'No vas a poder emitir facturas. Lo podés activar más adelante.'}
                        </Typography>
                        {t.annualEstimate > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Total anual estimado: ${t.annualEstimate.toLocaleString('es-AR')}/año
                          </Typography>
                        )}
                        <Button fullWidth variant={selected ? 'contained' : 'outlined'} sx={{ mt: 2 }}
                          disabled={savingTier} onClick={() => handleSelectTier(t.tier)}>
                          {selected ? 'Seleccionado' : 'Elegir'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button variant="text" onClick={() => navigate('/dashboard')}>
            Volver al panel
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
